import { useCallback, useEffect, useState, type ReactNode } from "react";
import { CheckIcon, ChevronDownIcon, CopyIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type Status = "online" | "starting" | "asleep" | "offline";
interface Mod { name: string; url: string | null }
interface Info { status: Status; players: { online: number; max: number }; mods: Mod[] }

const ADDRESS = "mc.behrad.space";
interface PackInfo { version: string; mrpack: { file: string; size: string }; zip: { file: string; size: string } }
// Published next to the downloads by the release script; the page never hardcodes a version.
function usePack() {
  const [pack, setPack] = useState<PackInfo | null>(null);
  useEffect(() => {
    fetch("/pack.json").then((r) => (r.ok ? r.json() : null)).then(setPack).catch(() => setPack(null));
  }, []);
  return pack;
}
const FORGE_INSTALLER = "https://maven.minecraftforge.net/net/minecraftforge/forge/1.20.1-47.4.10/forge-1.20.1-47.4.10-installer.jar";
const FORGE_PAGE = "https://files.minecraftforge.net/net/minecraftforge/forge/index_1.20.1.html";

function useInfo() {
  const [info, setInfo] = useState<Info | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await fetch("/admin/api/public/info");
      if (!r.ok) throw new Error(String(r.status));
      setInfo(await r.json());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
    const t = setInterval(() => document.visibilityState === "visible" && void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);
  return { info, failed };
}

function CopyAddress() {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
    } catch {
      const ta = Object.assign(document.createElement("textarea"), { value: ADDRESS });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setDone(true);
    toast.success("Copied");
    setTimeout(() => setDone(false), 1600);
  };
  return (
    <InputGroup className="h-11">
      <InputGroupInput readOnly value={ADDRESS} aria-label="Server address" className="font-mono text-base md:text-sm" onFocus={(e) => e.currentTarget.select()} />
      <InputGroupAddon align="inline-end">
        <InputGroupButton size="icon-sm" aria-label="Copy server address" onClick={copy}>
          {done ? <CheckIcon className="text-primary" /> : <CopyIcon />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

function StatusLine({ info, failed }: { info: Info | null; failed: boolean }) {
  if (!info) return failed ? <Line dot="bg-destructive">Offline</Line> : <Skeleton className="h-5 w-48" />;
  const { status, players } = info;
  if (status === "online") return <Line dot="bg-success" pulse>Online, {players.online}/{players.max || "?"} players</Line>;
  if (status === "starting") return <Line dot="bg-warning" pulse>Starting, try again in a minute</Line>;
  if (status === "asleep") return <Line dot="bg-muted-foreground">Asleep, joins wake it</Line>;
  return <Line dot="bg-destructive">Offline</Line>;
}
const Line = ({ dot, pulse, children }: { dot: string; pulse?: boolean; children: ReactNode }) => (
  <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
    <span className={cn("size-2 rounded-full", dot, pulse && "animate-pulse")} aria-hidden />{children}
  </p>
);

const Step = ({ n, children }: { n: number; children: ReactNode }) => (
  <li className="flex gap-3">
    <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border font-mono text-xs text-muted-foreground">{n}</span>
    <div className="grid min-w-0 gap-3 text-muted-foreground [&_strong]:font-medium [&_strong]:text-foreground">{children}</div>
  </li>
);
const A = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">{children}</a>
);
const Download = ({ href, children }: { href: string; children: ReactNode }) => (
  <Button size="lg" className="h-10 w-full sm:w-fit" nativeButton={false} render={<a href={href} download />}>{children}</Button>
);

function Fold({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Collapsible className="rounded-xl border">
      <CollapsibleTrigger className="group flex min-h-12 w-full items-center justify-between gap-3 px-4 text-left font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50 rounded-xl">
        {title}
        <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[panel-open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent><div className="border-t px-4 py-4 text-sm">{children}</div></CollapsibleContent>
    </Collapsible>
  );
}

const MEMORY = "The default 2 GB is not enough for about 80 mods.";

export default function Public() {
  const pack = usePack();
  const { info, failed } = useInfo();
  return (
    <main className="mx-auto grid max-w-xl gap-8 px-4 py-10 sm:py-16">
      <Toaster />
      <header className="grid gap-3">
        <h1 className="text-4xl font-semibold tracking-tight">craftkit</h1>
        <CopyAddress />
        <StatusLine info={info} failed={failed} />
      </header>

      <section aria-labelledby="join" className="grid gap-4">
        <h2 id="join" className="text-xl font-semibold tracking-tight">How to join</h2>
        <ol className="grid gap-5 text-sm">
          <Step n={1}>
            <p>Download the pack.</p>
            {pack ? <Download href={"/" + pack.mrpack.file}>{pack.mrpack.file} <span className="opacity-70">{pack.mrpack.size}</span></Download> : <Skeleton className="h-9 w-48" />}
          </Step>
          <Step n={2}>
            <p>Import it in <strong>Prism Launcher</strong> or the <strong>Modrinth app</strong>. It installs Forge and all the mods. Then set the instance memory to <strong>6 GB</strong> (4 GB minimum). {MEMORY}</p>
          </Step>
          <Step n={3}>
            <p>Join <strong className="font-mono">{ADDRESS}</strong>. You have to be whitelisted, so ask the admin first.</p>
          </Step>
        </ol>
      </section>

      <div className="grid gap-3">
        <Fold title="Manual install">
          <ol className="grid list-decimal gap-3 pl-5 text-muted-foreground marker:text-muted-foreground [&_strong]:font-medium [&_strong]:text-foreground">
            <li>Install <strong>Forge 1.20.1, version 47.4.10</strong>: <A href={FORGE_INSTALLER}>installer</A> (<A href={FORGE_PAGE}>all Forge downloads</A>).</li>
            <li>Download <a className="text-primary underline-offset-4 hover:underline" href={pack ? "/" + pack.zip.file : undefined} download>{pack ? pack.zip.file : "client mods zip"}</a>{pack ? ` (${pack.zip.size})` : ""} and unzip it into your <code className="font-mono">mods</code> folder.</li>
            <li>Set the memory to 6 GB (4 GB minimum). {MEMORY}</li>
          </ol>
        </Fold>
        <Fold title={`Mods on the server${info ? ` (${info.mods.length})` : ""}`}>
          {info ? (
            info.mods.length ? (
              <ul className="grid gap-x-6 sm:grid-cols-2">
                {info.mods.map((m, i) => (
                  <li key={m.name + i} className="border-b border-border/60 last:border-b-0">
                    {m.url ? (
                      <a href={m.url} target="_blank" rel="noopener noreferrer" className="block min-h-10 py-2 break-words text-foreground hover:text-primary">{m.name}</a>
                    ) : (
                      <span className="block min-h-10 py-2 break-words text-muted-foreground">{m.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">The mod list is not available right now.</p>
            )
          ) : failed ? (
            <p className="text-muted-foreground">Could not load the mod list.</p>
          ) : (
            <div className="grid gap-2"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-5 w-1/2" /><Skeleton className="h-5 w-3/5" /></div>
          )}
        </Fold>
      </div>

    </main>
  );
}
